import {
  api,
  searchApi,
  conversationsApi,
} from "assets/scripts/utilities/api-clients";
import * as authApi from "src/api/auth";

import {
  getAccessToken,
  getAccessTokenPayloadSecurely,
} from "supertokens-web-js/recipe/session";

const coreApiDomain = import.meta.env.CLIENT_API_DOMAIN;

const getInitialState = () => {
  return {
    isLoggedIn: false,
    user: {},
    permissions: {},
  };
};

function formatPermissions(permissions) {
  return permissions.reduce((acc, item) => {
    const { usecase, permissions } = item;

    if (!acc[usecase.code]) {
      acc[usecase.code] = {};
    }

    for (const permission of permissions) {
      acc[usecase.code][permission.code] = permission.allowed;
    }

    return acc;
  }, {});
}

export const useAuthStore = defineStore("auth", () => {
  let state = reactive(getInitialState());

  const init = async () => {
    try {
      const accessToken = await getAccessToken();

      if (!api.defaults.baseURL.includes(coreApiDomain)) {
        api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

        searchApi.defaults.headers.common["Authorization"] =
          `Bearer ${accessToken}`;

        conversationsApi.defaults.headers.common["Authorization"] =
          `Bearer ${accessToken}`;
      }

      await getUserInfo();

      const { init } = useAppStore();

      await init();
    } catch (error) {
      console.error(error);
    }
  };

  const requestOtp = async (phone) => {
    try {
      let response;

      if (await authApi.hasInitialOTPBeenSent()) {
        response = await authApi.resendOTP();
      } else {
        response = await authApi.sendOTP({ phone });
      }

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const login = async ({ phone, otp }) => {
    try {
      const response = await authApi.login({ phone, otp });

      if (response.status === 200) {
        await init();
      }

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getUserInfo = async () => {
    try {
      const accessTokenPayload = await getAccessTokenPayloadSecurely();

      const userId = accessTokenPayload?.["poshn-user-id"];

      if (userId) {
        const [response, permissionsResponse] = await Promise.all([
          authApi.getUser(),
          authApi.getUserRoles(),
        ]);

        const permissionsData = permissionsResponse?.data;

        if (
          response.data.status === 200 &&
          permissionsData?.status?.toLowerCase() == "success"
        ) {
          Object.assign(state, {
            isLoggedIn: true,
            user: response?.data?.data,
            permissions: formatPermissions(
              permissionsData?.response?.[0]?.rolesPermissions,
            ),
          });

          return response?.data?.data;
        }
      }
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const reset = () => {
    Object.assign(state, getInitialState());
  };

  const logout = async () => {
    await authApi.logout();

    if (!api.defaults.baseURL.includes(coreApiDomain)) {
      delete api.defaults.headers.common["Authorization"];

      delete searchApi.defaults.headers.common["Authorization"];

      delete conversationsApi.defaults.headers.common["Authorization"];
    }

    reset();
  };

  return {
    ...toRefs(state),

    init,

    login,
    logout,
    requestOtp,

    reset,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
